/**
 * Currency formatting utilities
 * Provides consistent currency display across the application
 * Always uses ₹ (Indian Rupee) symbol with Indian locale formatting
 */

/**
 * Formats a number as currency with ₹ symbol
 * @param {number|string|null|undefined} amount - Amount to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.showSymbol - Whether to show ₹ symbol (default: true)
 * @param {number} options.minimumFractionDigits - Minimum decimal places (default: 0)
 * @param {number} options.maximumFractionDigits - Maximum decimal places (default: 0)
 * @returns {string} - Formatted currency string (e.g., "₹1,23,456")
 */
export const formatCurrency = (amount, options = {}) => {
  const {
    showSymbol = true,
    minimumFractionDigits = 0,
    maximumFractionDigits = 0
  } = options;

  // Handle null, undefined, or invalid values
  if (amount === null || amount === undefined || amount === '') {
    return showSymbol ? '₹0' : '0';
  }

  // Convert to number
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Handle NaN or invalid numbers
  if (isNaN(numAmount) || !isFinite(numAmount)) {
    return showSymbol ? '₹0' : '0';
  }

  // Format with Indian locale
  const formatted = numAmount.toLocaleString('en-IN', {
    minimumFractionDigits,
    maximumFractionDigits,
    style: 'decimal'
  });

  return showSymbol ? `₹${formatted}` : formatted;
};

/**
 * Formats currency without symbol (just the number)
 * @param {number|string|null|undefined} amount - Amount to format
 * @returns {string} - Formatted number string (e.g., "1,23,456")
 */
export const formatCurrencyAmount = (amount) => {
  return formatCurrency(amount, { showSymbol: false });
};

